import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { Alert, Container, Paper, Stack, Text, Title } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';

function Login() {
  const { user, isAuthenticated, loginWithGoogleIdToken, loading } = useAuth();
  const navigate = useNavigate();
  const btnRef = useRef(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate(user?.onboarding_completed ? '/dashboard' : '/onboarding', { replace: true });
    }
  }, [isAuthenticated, loading, navigate, user]);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      setError('Missing REACT_APP_GOOGLE_CLIENT_ID. Add it to your frontend environment variables.');
      return;
    }

    const g = window.google;
    if (!g?.accounts?.id) {
      setError('Google Sign-In script failed to load.');
      return;
    }

    const renderButton = () => {
      if (!btnRef.current) return;
      
      // Clear any existing button
      btnRef.current.innerHTML = '';
      
      // Make button responsive - use full width on mobile, fixed width on desktop
      const isMobile = window.innerWidth < 768;
      g.accounts.id.renderButton(btnRef.current, {
        theme: 'outline',
        size: 'large',
        shape: 'pill',
        text: 'signin_with',
        width: isMobile ? null : 320, // null = full width on mobile
      });
    };

    try {
      g.accounts.id.initialize({
        client_id: clientId,
        callback: async (resp) => {
          try {
            setError('');
            await loginWithGoogleIdToken(resp.credential);
            // refreshMe already ran inside loginWithGoogleIdToken; user is now in context
            // We'll let the useEffect above handle the redirect based on onboarding state
          } catch (e) {
            setError(e?.response?.data?.detail || e.message || 'Login failed');
          }
        },
      });

      renderButton();

      // Re-render button on window resize for responsive behavior
      const handleResize = () => {
        renderButton();
      };
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    } catch (e) {
      setError(e?.message || 'Failed to initialize Google Sign-In');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container size="xs" py="xl" px="md">
      <Paper radius="lg" p={{ base: 'md', sm: 'xl' }} withBorder style={{ backgroundColor: '#ffffff' }}>
        <Stack gap="md">
          <div>
            <Title order={2} size="h3" ta="center">
              Sign in
            </Title>
            <Text c="dimmed" size="sm" mt={6} ta="center">
              Use your Google account to access your data.
            </Text>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', width: '100%', padding: '0 8px' }}>
            <div ref={btnRef} style={{ width: '100%', maxWidth: '320px', minHeight: '44px' }} />
          </div>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" radius="md" variant="light">
              {error}
            </Alert>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}

export default Login;

